// Lockdown hardening: recovery codes, the lockdown test and maintenance access at the PC (2026-10-01; F-LCK-06, F-LCK-09,
// F-LCK-11, F-STN-21; spec/11 §4.3, §6, §7, §9, spec/13 §12).
//
//   POST …/venues/{v}/stations/{s}/recovery-code        issue the one-time code `station-cli undo` needs    stations:recovery
//   POST …/venues/{v}/stations/{s}/lockdown-test        run the station's lockdown self-test               stations:manage
//   GET  …/venues/{v}/stations/{s}/lockdown-test        the latest self-test result                        stations:read
//   POST /v1/station/lockdown-test/results              the station reports a self-test (device token)
//   POST /v1/station/maintenance/sign-in                staff sign-in at the PC's lock screen (device token)
//   POST /v1/station/maintenance/events                 the station reports maintenance entered / ended (device token)
//
// A RECOVERY CODE is a compact JWS the api signs with the same JWKS the station verifies its policy bundle with
// (`aud` = `station-recovery`, `stationId`, `action: undo`, `jti`, `exp` 15 minutes). The station checks it OFFLINE
// against the keys it cached, refuses a reused `jti`, and only then undoes the lockdown (spec/11 §4.3). The code is
// returned once (`Cache-Control: no-store`); issuing it is audited (`stations.recovery_code.issued`), the code itself
// never.
//
// The LOCKDOWN TEST sends `station.lockdown.test { testId }`; the station runs its self-test — the escape suite's policy
// model over the locked posture plus its live read-backs — and reports each check. It does not press keys: the manual
// VM escape suite does that (infra/runbooks/lockdown-escape-suite.md).
//
// MAINTENANCE AT THE PC: "Staff" on the lock screen → the member's e-mail and staff PIN (spec/07 §6.7), checked here
// with `stations:maintenance` at the station's venue; refused while a session runs unless the member also holds
// `stations:maintenance_in_session` (the player is then warned). The window is `locker.maintenanceMinutes`. Entry and
// exit are audited with actor, station and route; the station reports the exits (relock, timeout, session started,
// console) and the offline-code entries it made while the venue was offline.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { MemberIdSchema, OfflineStaffCodeIdSchema, StationIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { STATIONS_MANAGE, STATIONS_READ, STATIONS_RECOVERY } from "../permissions/index.js";
import { StationModeSchema } from "../realtime/station.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { StationActionParamsSchema } from "./floor.js";
import { StaffPinSchema } from "./staff-pin.js";
/** The `aud` of a recovery code — never a bundle, lease, library or token audience. The station requires it. */
export const STATION_RECOVERY_AUDIENCE = 'station-recovery';
/** How long a recovery code is valid, in seconds (15 minutes). */
export const STATION_RECOVERY_CODE_TTL_SECONDS = 15 * 60;
/** The default and the longest maintenance window (spec/11 §7, §10 `locker.maintenanceMinutes`). */
export const MAINTENANCE_MINUTES_DEFAULT = 30;
export const MAINTENANCE_MINUTES_MAX = 240;
const STATION_AUTH = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The station provides its own ' +
    'organisation, venue and station from the token.';
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const stationNotFound = problemResponse('No such station at this venue.', 'not-found');
// ---------------------------------------------------------------------------------------------
// Recovery codes (F-LCK-06, spec/11 §4.3)
// ---------------------------------------------------------------------------------------------
export const StationRecoveryCodeSchema = z
    .object({
    /** The code to paste into `Clavian.Station.Service.exe undo --code <code>` on the PC. Shown once. */
    code: z.string().min(20),
    /** The code's id (`jti`) — what the audit row and the station's used-code ledger name. */
    codeId: z.string().min(1),
    /** When it stops working. */
    expiresAt: IsoDateTimeSchema,
})
    .openapi('StationRecoveryCode', { description: 'A one-time, 15-minute recovery code for `station-cli undo` on one station (spec/11 §4.3).' });
export const issueStationRecoveryCodeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/recovery-code',
    operationId: 'issueStationRecoveryCode',
    tags: ['stations'],
    summary: 'Issue a recovery code for station-cli undo',
    description: 'Issue a one-time code that lets an administrator run `station-cli undo` on this station for the next 15 minutes ' +
        '(spec/11 §4.3). The code is signed by the api and verified offline on the PC; it is returned once ' +
        '(`Cache-Control: no-store`) and never stored. Requires `stations:recovery`. Audited.',
    [PERMISSION_EXTENSION]: STATIONS_RECOVERY,
    request: { params: StationActionParamsSchema },
    responses: {
        201: { description: 'The code, shown once.', content: { [JSON_CONTENT_TYPE]: { schema: StationRecoveryCodeSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        409: problemResponse('The station is not paired, so no code can name it.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The lockdown test (F-STN-21, spec/13 §12, spec/11 §6, §9)
// ---------------------------------------------------------------------------------------------
export const LockdownCheckOutcomeSchema = z
    .enum(['pass', 'fail', 'not_applicable'])
    .openapi('LockdownCheckOutcome', { description: 'Whether one lockdown check held.' });
export const LockdownCheckSchema = z
    .object({
    /** A stable id: an escape vector (`§9.4`) or a live check (`live.policy`). */
    id: z.string().min(1).max(40),
    name: z.string().min(1).max(200),
    outcome: LockdownCheckOutcomeSchema,
    /** Why — the policy that blocks it, or what did not read back. */
    detail: z.string().max(1000),
})
    .openapi('LockdownCheck');
export const LockdownTestStatusSchema = z
    .enum(['pending', 'passed', 'failed', 'expired'])
    .openapi('LockdownTestStatus', { description: 'Where a lockdown test is: waiting for the station, passed, failed, or never answered.' });
export const StationLockdownTestSchema = z
    .object({
    testId: z.string().uuid(),
    stationId: StationIdSchema,
    status: LockdownTestStatusSchema,
    requestedAt: IsoDateTimeSchema,
    requestedBy: MemberIdSchema.nullable(),
    /** When the station ran it; null while pending. */
    ranAt: IsoDateTimeSchema.nullable(),
    /** The mode the station was in when it ran. */
    mode: StationModeSchema.nullable(),
    checks: z.array(LockdownCheckSchema),
})
    .openapi('StationLockdownTest', { description: 'A lockdown self-test on one station and its result per check (F-STN-21).' });
export const runStationLockdownTestRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/lockdown-test',
    operationId: 'runStationLockdownTest',
    tags: ['stations'],
    summary: 'Run the lockdown test on a station',
    description: 'Send `station.lockdown.test` to the station: it runs its lockdown self-test and reports each check (F-STN-21, ' +
        'spec/13 §12). Returns the pending test; read the result with GET. Requires `stations:manage`. Audited.',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: { params: StationActionParamsSchema },
    responses: {
        202: { description: 'The test was sent.', content: { [JSON_CONTENT_TYPE]: { schema: StationLockdownTestSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        429: commonProblemResponses[429],
        500: problemResponse('The gateway is not available to deliver commands right now.', 'internal-error'),
    },
});
export const getStationLockdownTestRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/lockdown-test',
    operationId: 'getStationLockdownTest',
    tags: ['stations'],
    summary: 'The latest lockdown test on a station',
    description: 'The newest lockdown test on the station and its checks (F-STN-21). Requires `stations:read`.',
    [PERMISSION_EXTENSION]: STATIONS_READ,
    request: { params: StationActionParamsSchema },
    responses: {
        200: { description: 'The newest test.', content: { [JSON_CONTENT_TYPE]: { schema: StationLockdownTestSchema } } },
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such station, or it has never been tested.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const ReportStationLockdownTestSchema = z
    .object({
    testId: z.string().uuid(),
    mode: StationModeSchema,
    ranAt: IsoDateTimeSchema,
    checks: z.array(LockdownCheckSchema).min(1).max(64),
})
    .strict()
    .openapi('ReportStationLockdownTest');
export const reportStationLockdownTestRoute = createRoute({
    method: 'post',
    path: '/v1/station/lockdown-test/results',
    operationId: 'reportStationLockdownTest',
    tags: ['stations'],
    summary: 'Report a lockdown self-test (station)',
    description: `${STATION_AUTH} The result of a \`station.lockdown.test\` the console asked for. A test id the station was not sent is refused.`,
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: ReportStationLockdownTestSchema } }, required: true } },
    responses: {
        204: { description: 'Recorded.' },
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such pending test for this station.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Maintenance access at the PC (F-LCK-09, spec/11 §7)
// ---------------------------------------------------------------------------------------------
export const MaintenanceRouteSchema = z
    .enum(['pc_sign_in', 'remote_command', 'offline_code'])
    .openapi('MaintenanceRoute', { description: 'How staff entered maintenance (spec/11 §7).' });
export const MaintenanceEndReasonSchema = z
    .enum(['relock', 'timeout', 'session_started', 'remote'])
    .openapi('MaintenanceEndReason', { description: 'Why maintenance ended (spec/11 §7).' });
export const StationMaintenanceSignInSchema = z
    .object({
    /** The staff member's e-mail, as typed at the lock screen. */
    email: z.string().trim().email().max(254),
    pin: StaffPinSchema,
})
    .strict()
    .openapi('StationMaintenanceSignIn', { description: 'Staff sign-in at the PC for maintenance: e-mail and staff PIN. Never logged.' });
export const StationMaintenanceGrantSchema = z
    .object({
    memberId: MemberIdSchema,
    /** The name the banner shows. */
    memberName: z.string(),
    /** `locker.maintenanceMinutes` at this venue. */
    minutes: z.int().min(1).max(MAINTENANCE_MINUTES_MAX),
    until: IsoDateTimeSchema,
    /** A session was running and the member holds `stations:maintenance_in_session`: warn the player. */
    duringSession: z.boolean(),
})
    .openapi('StationMaintenanceGrant');
export const signInStationMaintenanceRoute = createRoute({
    method: 'post',
    path: '/v1/station/maintenance/sign-in',
    operationId: 'signInStationMaintenance',
    tags: ['stations'],
    summary: 'Staff sign-in at the PC for maintenance (station)',
    description: `${STATION_AUTH} Checks the member's e-mail and staff PIN at the station's venue (five wrong PINs lock it, spec/07 ` +
        '§6.7) and that they hold `stations:maintenance` there; refused while a session runs unless they also hold ' +
        '`stations:maintenance_in_session`. On success the station enters maintenance for `minutes` under a banner with ' +
        'the member\'s name. Audited (`stations.station.maintenance`, route `pc_sign_in`).',
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: StationMaintenanceSignInSchema } }, required: true } },
    responses: {
        200: { description: 'Granted.', content: { [JSON_CONTENT_TYPE]: { schema: StationMaintenanceGrantSchema } } },
        400: problemResponse('The request is malformed, or the e-mail and PIN do not match a staff member here.', 'validation-failed', 'staff-pin-incorrect'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        403: problemResponse('The member may not put this station into maintenance, or their PIN is locked.', 'forbidden', 'staff-pin-locked'),
        409: problemResponse('A session is running and the member may not interrupt it.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const StationMaintenanceEventSchema = z
    .object({
    /** `entered` (the station opened maintenance on its own — an offline code) or `ended`. */
    kind: z.enum(['entered', 'ended']),
    route: MaintenanceRouteSchema,
    /** The member it is attributed to; null for an offline code whose issuer the PC does not know. */
    memberId: MemberIdSchema.nullable(),
    /** The offline staff code, for `entered` by `offline_code`. */
    codeId: OfflineStaffCodeIdSchema.nullable().optional(),
    /** When the window ends, for `entered`. */
    until: IsoDateTimeSchema.optional(),
    /** Why it ended, for `ended`. */
    reason: MaintenanceEndReasonSchema.optional(),
    /** For `ended`: whether the relock's read-back passed (spec/11 §6); false keeps the cover up and raises `policy_apply_failed`. */
    relockVerified: z.boolean().optional(),
    at: IsoDateTimeSchema,
})
    .strict()
    .refine((event) => (event.kind === 'entered' ? event.until !== undefined : event.reason !== undefined && event.relockVerified !== undefined), {
    message: 'An entry names when it ends; an exit names why and whether the relock verified.',
})
    .openapi('StationMaintenanceEvent');
export const reportStationMaintenanceEventRoute = createRoute({
    method: 'post',
    path: '/v1/station/maintenance/events',
    operationId: 'reportStationMaintenanceEvent',
    tags: ['stations'],
    summary: 'Report maintenance entered or ended (station)',
    description: `${STATION_AUTH} Audits a maintenance entry the station made on its own (an offline code) and every exit — relock, ` +
        'timeout, session started or the console — with the member, the route and whether the relock verified.',
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: StationMaintenanceEventSchema } }, required: true } },
    responses: {
        204: { description: 'Audited.' },
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-lockdown.js.map